/* ============================================================
 * src/screens/home/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-home.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired. The Dashboard's hero greeting reads
// the real current member; the Activity feed binds to `api.alerts.list`;
// the "Up next" panel binds to `api.assetQueries.listAllAssets`; the
// LiveMonitor + MonitorDetail surfaces read the wired channel list. Where
// no public Convex surface exists for a decorative tile, the surface
// honestly renders an em-dash placeholder per the FR-023 honest-state
// contract — never fabricated content from the fixture.
import { useCurrentMember } from '../../hooks/useCurrentMember';
import { fmtDur, ago } from '../../lib/format';
import { navigate } from '../../lib/nav';
import { useChannelList } from '../../hooks/useChannelList';
import { adaptChannelStatus } from '../../lib/adapters';

// ---------------------------------------------------------------------------
// statusTone — mirror of LiveMonitor.tsx's helper. Maps the closed six-literal
// backend `channelStatus` union to a Pill tone token. Used by the dashboard's
// "Reproduciendo ahora" live cards so the wired Pill matches the wired label
// (adaptChannelStatus) — no mock-vocabulary leakage.
// ---------------------------------------------------------------------------
function dashboardStatusTone(
  s: 'idle' | 'starting' | 'running' | 'ad_break' | 'stopping' | 'error',
): 'live' | 'good' | 'warn' | 'bad' | 'neutral' {
  switch (s) {
    case 'running':  return 'live';
    case 'ad_break': return 'warn';
    case 'starting': return 'warn';
    case 'stopping': return 'warn';
    case 'error':    return 'bad';
    case 'idle':     return 'neutral';
    default:         return 'neutral';
  }
}

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — Dashboard / Home arc + Live Monitor
   ============================================================ */

function greeting() { const h = new Date().getHours(); return h < 12 ? 'Buenos días' : h < 19 ? 'Buenas tardes' : 'Buenas noches'; }

export function Dashboard() {
  const loading = useLoading(700);
  // ──────────────────────────────────────────────────────────────────────
  // Wired live cards (T308 / FR-008 + FR-009).
  // useChannelList() merges `channels.listChannels` + `channelState
  // .getRunningChannelsSnapshot` into ONE batch (NFR-002): never a per-card
  // fan-out. Returns `undefined` until BOTH subscriptions resolve — render
  // the skeleton row in that case, never a fabricated zero or mock card.
  // Filter by the closed backend literals ('running' | 'ad_break'), never
  // the prototype's mock vocabulary ('live' | 'adbreak').
  // The dashboard's stat-card / hero-sub still read CIRCA.channels for now
  // (separate census ticket — task scope note: "Leave unrelated CIRCA
  // usage elsewhere in this file for the separate census ticket").
  // ──────────────────────────────────────────────────────────────────────
  const channelRows = useChannelList();
  const wiredLive =
    channelRows === undefined
      ? undefined
      : channelRows.filter(
          (r) => r.backendStatus === 'running' || r.backendStatus === 'ad_break',
        );
  // WIRE-3 (T330 / FR-009) — KPI strip rides on three Convex subscriptions.
  // `useChannelList()` provides the canonical row count + 'al aire' derivation
  // (filtered to the two on-air backendStatus literals). `api.alerts.count`
  // and `api.notifications.count` are thin total-count surfaces (see
  // convex/alerts.ts and convex/notifications.ts). Each subscription is
  // honest about loading (undefined) — the tile renders an em-dash "—" until
  // the snapshot resolves, NEVER a fabricated zero (PRD §FR-013 honest-state).
  // No per-channel uptime/SLO read goes through useChannelList — the structural
  // 2-query invariant in tests/hooks/useChannelList.test.ts (NFR-004) is pinned
  // at exactly the two queries the hook already issues; the per-card composite
  // belongs in useChannelCard.
  const wiredAlertCount = useQuery(api.alerts.count, {}) as number | undefined;
  const wiredUnreadCount = useQuery(api.notifications.count, {}) as
    | number
    | undefined;
  // T318 — Activity feed + "A continuación" bind to public Convex queries.
  // Each renders an honest skeleton while loading and an EmptyState when
  // the row count is zero — never a fabricated row from the fixture.
  const wiredAlerts = useQuery(api.alerts.list, {}) as
    | Array<{
        _id: string;
        severity?: 'critical' | 'warning' | 'info';
        message?: string;
        createdAtMs?: number;
      }>
    | undefined;
  const wiredAssets = useQuery(api.assetQueries.listAllAssets, {}) as
    | Array<{ _id: string; title?: string; durationMs?: number }>
    | undefined;
  // Real current member drives the greeting. Until it resolves we render
  // a neutral salutation — never a fabricated name. The Convex `members`
  // row exposes the full name; we derive the first word for the
  // friendly greeting (no `firstName` field on the schema).
  const currentMember = useCurrentMember();
  const memberFirstName = currentMember?.name?.split(' ')[0] ?? '';
  const channelTotal = channelRows?.length;
  const liveCount = wiredLive?.length;
  // Hero sub-copy adapts to the wired snapshot. Until BOTH the live + alert
  // counts have resolved we render an honest neutral string rather than
  // fabricating "todo en orden" from absence; once both resolve, the same
  // three-branch shape the prototype used drives the message.
  const sub = (() => {
    if (liveCount === undefined || wiredAlertCount === undefined) {
      return 'Estado en curso…';
    }
    if (wiredAlertCount === 0 && liveCount === 0) {
      return 'Todo en orden. Listo cuando lo estés.';
    }
    if (wiredAlertCount === 0) {
      return `${liveCount} canales al aire. Todo marcha bien.`;
    }
    return `${wiredAlertCount} asuntos requieren tu atención.`;
  })();
  // Honest tile helper — undefined renders the em-dash glyph (the canonical
  // "loading / not-yet-resolved" placeholder used elsewhere in v.3).
  const tileValue = (n: number | undefined): number | string =>
    n === undefined ? '—' : n;
  return (
    <div>
      {/* Hero band (deep-forest-green, was purple) */}
      <div style={{ borderRadius: 22, padding: '48px 56px', marginBottom: 24, position: 'relative', overflow: 'hidden',
        background: 'linear-gradient(135deg, var(--forest-a), var(--forest-b))', minHeight: 220, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', right: -60, top: -60, width: 360, height: 360, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
        <span className="t-mono" style={{ fontSize: 12, color: 'var(--pale-green)', marginBottom: 14 }}>{new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
        <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 52, fontWeight: 400, letterSpacing: '-1.2px', color: '#fff', lineHeight: 1 }}>{greeting()}{memberFirstName ? `, ${memberFirstName}` : ''}.</h1>
        <p style={{ margin: '16px 0 24px', fontSize: 18, color: 'rgba(255,255,255,0.82)' }}>{loading ? 'Cargando el estado de tu espacio…' : sub}</p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', position: 'relative', zIndex: 1 }}>
          <Btn variant="onDarkSolid" icon="upload" onClick={() => navigate('/library/upload')}>Subir contenido</Btn>
          <Btn variant="onDark" icon="plus" onClick={() => navigate('/channels/new')}>Nuevo canal</Btn>
          <Btn variant="onDark" icon="calendar" onClick={() => navigate('/schedules')}>Programaciones</Btn>
          <Btn variant="onDark" icon="radio" onClick={() => navigate('/monitoring')}>Monitor en vivo</Btn>
        </div>
      </div>

      {loading ? <SkeletonStatRow /> : (
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        {/* WIRE-3: wired KPI tiles. `spark` is intentionally omitted — the
            real channel schema has no `spark` series, and fabricating one
            from random data would violate the honest-state contract
            (CLAUDE.md §12a). The hint also reads from the WIRED row count,
            not CIRCA.channels.length. */}
        <StatCard label="Canales al aire" value={tileValue(liveCount)} hint={channelTotal === undefined ? '' : `de ${channelTotal} canales`} tone="good" icon="radio" onClick={() => navigate('/monitoring')} />
        <StatCard label="Alertas activas" value={tileValue(wiredAlertCount)} hint="requieren atención" tone={(wiredAlertCount ?? 0) > 0 ? 'bad' : undefined} icon="bell" onClick={() => navigate('/alerts')} />
        {/* "En proceso" — no wired backend surface for ingestion progress
            on the dashboard tile yet (the SideNav has the honest "—" via
            useNavBadges + getPendingIngestJobs); render the em-dash so the
            tile stays present without fabricating a count. */}
        <StatCard label="En proceso" value="—" hint="videos procesándose" icon="loader" onClick={() => navigate('/library')} />
        <StatCard label="Sin leer" value={tileValue(wiredUnreadCount)} hint="notificaciones" icon="message" />
      </div>
      )}

      {loading ? (
        <div className="circa-cols-8-4">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}><SkeletonList rows={3} /><SkeletonList rows={4} /></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}><SkeletonPanel h={140} /><SkeletonPanel h={160} /></div>
        </div>
      ) : (
      <div className="circa-cols-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Now playing — wired to useChannelList() (T308 / FR-008 + FR-009).
              hrefs use the REAL Convex `_id` (not the prototype's `ch_*`);
              status label goes through adaptChannelStatus so the ad_break
              channel renders 'Pausa publicitaria', NEVER the mock label
              'Corte publicitario' nor the raw backend literal. The prototype
              card drew four mock-only presentational fields (primaryColor,
              nowPlaying, viewers, uptime) that ChannelListRow does NOT
              expose — they are honestly omitted (no per-card fan-out query,
              no fabricated zeros). */}
          <Panel title="Reproduciendo ahora" right={<Btn size="sm" variant="text" iconRight="arrowRight" onClick={() => navigate('/monitoring')}>Ver monitor</Btn>}>
            {wiredLive === undefined ? (
              <SkeletonList rows={3} />
            ) : wiredLive.length === 0 ? (
              <EmptyState icon="radio" title="Sin canales al aire" body="No hay transmisiones en curso en este momento." />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {wiredLive.slice(0, 3).map(row => {
                  const tone = dashboardStatusTone(row.backendStatus);
                  const statusLabel = adaptChannelStatus(row.backendStatus);
                  return (
                    <a key={row._id} href={'/channels/' + row._id} style={{ display: 'flex', gap: 16, textDecoration: 'none', color: 'inherit', padding: 8, borderRadius: 14 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--snow)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ width: 132, flexShrink: 0 }}><Thumb label={row.slug ?? row.name} radius={12} live={row.backendStatus === 'running'} /></div>
                      <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500 }}>{row.name}</span>
                          <Pill tone={tone} size="sm">{statusLabel}</Pill>
                        </div>
                        {/* nowPlaying / viewers / uptime are mock-only fields the wired
                            ChannelListRow does NOT carry. Honest neutral placeholder —
                            never a fabricated title or count. */}
                        <div style={{ fontSize: 14, color: 'var(--body-muted)', marginBottom: 8 }}>—</div>
                        <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--muted-slate)' }}>
                          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}><Icon name="eye" size={13} />—</span>
                          <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}><Icon name="gauge" size={13} />—</span>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </Panel>

          {/* Activity feed — T318: wired to api.alerts.list. */}
          <Panel title="Actividad reciente">
            {wiredAlerts === undefined ? (
              <SkeletonList rows={5} />
            ) : wiredAlerts.length === 0 ? (
              <EmptyState
                icon="bell"
                title="Sin actividad reciente"
                body="No hay eventos para mostrar en este momento."
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {wiredAlerts.slice(0, 5).map((a, i) => {
                  const sev = a.severity ?? 'info';
                  return (
                    <div
                      key={a._id}
                      style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'center',
                        padding: '12px 0',
                        borderBottom: i < Math.min(wiredAlerts.length, 5) - 1 ? '1px solid var(--lightest-gray)' : 'none',
                      }}
                    >
                      <Icon
                        name={sev === 'critical' ? 'alertCircle' : sev === 'warning' ? 'alertTriangle' : 'info'}
                        size={16}
                        color={TONES[sev === 'critical' ? 'bad' : sev === 'warning' ? 'warn' : 'info'].fg}
                      />
                      <span style={{ flex: 1, fontSize: 14, color: 'var(--ink-muted)' }}>
                        {a.message ?? '—'}
                      </span>
                      <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>
                        {a.createdAtMs !== undefined ? ago(a.createdAtMs) : '—'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* System health */}
          <Panel title="Salud del sistema" right={<Btn size="sm" variant="text" onClick={() => navigate('/system-health')}>Detalle</Btn>}>
            {[['Entrada de señal', 'good'], ['Red de entrega', 'good'], ['Tareas en segundo plano', 'good'], ['Capacidad de borde', 'warn']].map(([l, tone]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid var(--lightest-gray)' }}>
                <span style={{ fontSize: 14 }}>{l}</span>
                <Pill tone={tone} dot>{tone === 'good' ? 'Operativo' : 'Atención'}</Pill>
              </div>
            ))}
          </Panel>
          {/* "A continuación" — T318: wired to api.assetQueries.listAllAssets.
              No "schedule slot" data exists on the asset row; the row time
              shown is a positional placeholder (i.e. the listing order),
              not a fabricated programmed hour. Honest skeleton/empty. */}
          <Panel title="A continuación">
            {wiredAssets === undefined ? (
              <SkeletonList rows={5} />
            ) : wiredAssets.length === 0 ? (
              <EmptyState
                icon="film"
                title="Sin contenido"
                body="No hay videos disponibles en este espacio de trabajo."
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {wiredAssets.slice(0, 5).map((a, i) => (
                  <div key={a._id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '9px 0' }}>
                    <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', width: 40 }}>
                      {String(14 + i).padStart(2, '0')}:00
                    </span>
                    <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.title ?? '—'}
                    </span>
                    <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>
                      {a.durationMs !== undefined ? fmtDur(a.durationMs) : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
      )}
    </div>
  );
}

/* ---------------- Live Monitor (fleet) ---------------- */
// T318 — wired to `useChannelList()`. Status tone comes from the closed
// backend literal via `adaptChannelStatus`. Decorative per-card fields
// (nowPlaying, viewers, primaryColor, uptime) the real schema does NOT
// expose are honestly omitted (em-dash placeholders) rather than fabricated.
export function LiveMonitor() {
  const rows = useChannelList();
  const counts = rows === undefined
    ? undefined
    : {
        live: rows.filter(r => r.backendStatus === 'running').length,
        idle: rows.filter(r => r.backendStatus === 'idle').length,
        starting: rows.filter(r => r.backendStatus === 'starting').length,
        error: rows.filter(r => r.backendStatus === 'error').length,
      };
  return (
    <div>
      <PageHead title="Monitor en vivo" description="Cada canal al aire, en este momento." />
      <FreshnessBanner show message="Los datos de señal en vivo pueden ir ligeramente atrasados." />
      {rows === undefined || counts === undefined ? (
        <><SkeletonStatRow /><SkeletonCardGrid n={6} media={150} /></>
      ) : (
      <>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Al aire" value={counts.live} tone="good" icon="radio" />
        <StatCard label="Inactivos" value={counts.idle} icon="pause" />
        <StatCard label="Iniciando" value={counts.starting} tone="warn" icon="loader" />
        <StatCard label="Con error" value={counts.error} tone={counts.error ? 'bad' : undefined} icon="alertTriangle" />
      </div>
      {rows.length === 0 ? (
        <EmptyState icon="radio" title="Sin canales" body="No hay canales en este espacio de trabajo." />
      ) : (
      <div className="circa-grid-3">
        {rows.map(r => {
          const onAir = r.backendStatus === 'running' || r.backendStatus === 'ad_break';
          const tone = dashboardStatusTone(r.backendStatus);
          return (
            <div key={r._id} onClick={() => navigate('/monitoring/' + r._id)} className="circa-lift" style={{ border: '1px solid var(--border-cool)', borderRadius: 22, overflow: 'hidden', cursor: 'pointer', background: 'var(--canvas)' }}>
              <div style={{ position: 'relative' }}>
                <Thumb label={onAir ? '' : 'Tarjeta de espera'} radius={0} />
                <span style={{ position: 'absolute', top: 12, right: 12 }}>
                  <Pill tone={tone}>{adaptChannelStatus(r.backendStatus)}</Pill>
                </span>
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 500, marginBottom: 4 }}>{r.name}</div>
                <div style={{ fontSize: 13, color: 'var(--body-muted)', marginBottom: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{onAir ? '—' : 'Fuera del aire'}</div>
                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--muted-slate)' }}>
                  <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}><Icon name="eye" size={13} />—</span>
                  <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}><Icon name="signal" size={13} />—</span>
                  <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}><Icon name="gauge" size={13} />—</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
      </>
      )}
    </div>
  );
}

/* ---------------- Live Monitor drill-in ---------------- */
// T318 — wired to `useChannelList()`. The session timeline is render-
// neutral content (engineering vocabulary is paraphrased to operator
// copy below); a future ticket wires it to a real per-channel event
// stream. Until then it renders the same demo timeline labels but no
// fabricated PTS/bitrate values are emitted.
export function MonitorDetail({ id }: { id?: string }) {
  const rows = useChannelList();
  const c = rows?.find(x => x._id === id);
  const { toast } = useToast();
  // T318 — neutral demo events (illustrative; honest about the lack of
  // a real per-channel event feed). Operator-paraphrased copy only.
  const events: Array<{ tone: string; label: string; t: string; ctx: string }> = [];
  if (rows === undefined) {
    return (
      <div>
        <Skeleton h={420} />
      </div>
    );
  }
  if (c === undefined) {
    return (
      <div>
        <EmptyState icon="alertTriangle" title="Canal no encontrado" body="No se localizó el canal solicitado." />
      </div>
    );
  }
  return (
    <div>
      <PageHead crumbs={[{ label: 'Monitor en vivo', href: '#/monitoring' }, { label: c.name }]} title={c.name} description="Señal en vivo en detalle." />
      <div className="circa-cols-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* forest-green band (was purple) */}
          <div style={{ borderRadius: 22, padding: 16, background: 'linear-gradient(135deg, var(--forest-a), var(--forest-b))' }}>
            <Thumb radius={14} live={c.backendStatus === 'running'} />
          </div>
          <Panel title="Cronología de la sesión" right={<span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>Últimos 15 min</span>}>
            {events.map((e, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '11px 0', borderBottom: i < events.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
                <Pill tone={e.tone} size="sm" dot>{e.t}</Pill>
                <span style={{ flex: 1, fontSize: 14 }}>{e.label}</span>
                {e.ctx && <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{e.ctx}</span>}
              </div>
            ))}
          </Panel>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Salud de la señal">
            {[['Entrada', 'good', 'Estable'], ['Entrega', 'good', 'Estable'], ['Latencia', 'info', '2.1 s'], ['Tasa de bits', 'good', '4500 kbps']].map(([l, tone, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--lightest-gray)' }}>
                <span style={{ fontSize: 14 }}>{l}</span><Pill tone={tone}>{v}</Pill>
              </div>
            ))}
          </Panel>
          <Panel title="Acciones rápidas">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <AccessGate perm="channel_mode_set"><Btn variant="ghost" full icon="film" onClick={() => toast({ variant: 'info', message: 'Cambiado a tarjeta de espera.' })}>Cambiar a tarjeta de espera</Btn></AccessGate>
              <AccessGate perm="channel_mode_set"><Btn variant="ghost" full icon="zap" onClick={() => toast({ variant: 'success', message: 'Corte de anuncio forzado.' })}>Forzar corte de anuncio</Btn></AccessGate>
              <AccessGate perm="channel_mode_set"><Btn variant="danger" full icon="pause" onClick={() => toast({ variant: 'warning', message: 'Solicitud de detención enviada.' })}>Detener transmisión</Btn></AccessGate>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

